/**
 * Preorder cart validation
 *
 * Prevents mixing preorder products (tagged 'preorder') with regular products in the cart.
 * Works by intercepting add-to-cart form submissions and checking the current cart state.
 *
 * Preorder items are marked at add-time with a hidden line item property `_preorder=1`
 * (added via buy-buttons.liquid), which lets us identify them in subsequent /cart.js checks.
 */
(function () {
  'use strict';

  const PREORDER_PROP = '_preorder';

  const LINK = 'color:inherit;text-decoration:underline;';

  const MSG = {
    addingPreorderToRegularCart:
      'Pre-order items must be purchased separately. ' +
      'Please complete your current order or <a href="/cart" style="' + LINK + '">clear your cart</a> first.',
    addingRegularToPreorderCart:
      'Your cart contains a pre-order item. ' +
      'Please complete that order or <a href="/cart" style="' + LINK + '">clear your cart</a> before adding regular items.',
  };


  function getOrCreateErrorEl(productFormEl) {
    let el = productFormEl.querySelector('.preorder-error');
    if (!el) {
      el = document.createElement('div');
      el.className = 'preorder-error';
      el.setAttribute('role', 'alert');
      el.style.cssText = [
        'display:flex',
        'align-items:flex-start',
        'gap:0.5rem',
        'background:#FDF3E3',
        'color:#5C3322',
        'font-size:0.875rem',
        'font-weight:600',
        'line-height:1.4',
        'padding:0.875rem 1.25rem',
        'border-radius:0.75rem',
        'margin-top:0.75rem',
        'margin-bottom:0.75rem',
      ].join(';');
      productFormEl.prepend(el);
    }
    return el;
  }

  function clearError(productFormEl) {
    const el = productFormEl.querySelector('.preorder-error');
    if (el) el.innerHTML = '';
  }

  function showError(productFormEl, html) {
    getOrCreateErrorEl(productFormEl).innerHTML =
      '<span aria-hidden="true" style="flex-shrink:0;font-style:normal;">ⓘ</span>' +
      '<span>' + html + '</span>';
  }

  /**
   * Determine whether every item in the cart array is preorder, regular, or mixed.
   * Returns { hasPreorder, hasRegular }.
   *
   * Items stamped with _preorder=1 are identified immediately. Items without the
   * property (added before this code was deployed, or via non-form routes) fall back
   * to a /products/{handle}.js fetch to check the 'preorder' tag directly.
   */
  async function inspectCart(items) {
    let hasPreorder = false;
    let hasRegular = false;
    const needsLookup = [];

    for (const item of items) {
      if (item.properties && item.properties[PREORDER_PROP] === '1') {
        hasPreorder = true;
      } else if (item.properties && item.properties[PREORDER_PROP] === undefined
                 || !item.properties) {
        needsLookup.push(item.handle);
      } else {
        hasRegular = true;
      }
    }

    if (needsLookup.length > 0) {
      const uniqueHandles = [...new Set(needsLookup)];
      const results = await Promise.all(
        uniqueHandles.map((handle) =>
          fetch(`/products/${handle}.js`)
            .then((r) => r.json())
            .then((p) => ({ handle, isPreorder: Array.isArray(p.tags) && p.tags.includes('preorder') }))
            .catch(() => ({ handle, isPreorder: false }))
        )
      );
      const preorderHandles = new Set(results.filter((r) => r.isPreorder).map((r) => r.handle));
      for (const handle of uniqueHandles) {
        if (preorderHandles.has(handle)) hasPreorder = true;
        else hasRegular = true;
      }
    }

    return { hasPreorder, hasRegular };
  }

  // Intercept all form submissions in capture phase so we run before the theme's ProductForm handler.
  document.addEventListener(
    'submit',
    async function (event) {
      const form = event.target;

      // Only act on product add-to-cart forms
      if (!form.matches('form[action*="/cart/add"]')) return;

      // Second pass after our own validation — let it through
      if (form.dataset.preorderOk === '1') return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const productFormEl = form.closest('product-form');
      const isPreorder = productFormEl?.dataset.preorder === 'true';

      try {
        const cart = await fetch('/cart.js').then((r) => r.json());

        if (cart.items.length > 0) {
          const { hasPreorder, hasRegular } = await inspectCart(cart.items);

          if (isPreorder && hasRegular) {
            showError(productFormEl || form, MSG.addingPreorderToRegularCart);
            return;
          }

          if (!isPreorder && hasPreorder) {
            showError(productFormEl || form, MSG.addingRegularToPreorderCart);
            return;
          }
        }

        // All clear — dismiss any prior error and re-submit so ProductForm handles it
        if (productFormEl) clearError(productFormEl);
        form.dataset.preorderOk = '1';
        form.requestSubmit(form.querySelector('[type="submit"]') ?? undefined);
      } catch (_err) {
        // On network error fall through to normal submission rather than silently blocking
        form.dataset.preorderOk = '1';
        form.requestSubmit(form.querySelector('[type="submit"]') ?? undefined);
      } finally {
        // Clean up the flag after the re-submitted event has propagated
        setTimeout(() => {
          delete form.dataset.preorderOk;
        }, 300);
      }
    },
    true // capture phase — fires before bubble-phase handlers (including ProductForm)
  );
})();
